import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import {
  TableRow,
  TableRowColumn,
} from 'material-ui/Table';
import DataTables from 'material-ui-datatables';
import IconButton from 'material-ui/IconButton';
import Chip from 'material-ui/Chip';
import Avatar from 'material-ui/Avatar';
import FloatingActionButton from 'material-ui/FloatingActionButton';
import ContentAdd from 'material-ui/svg-icons/content/add';
import FontIcon from 'material-ui/FontIcon';
import FlatButton from 'material-ui/FlatButton';
import _ from 'lodash';

import Icon from '../Common/Icon';
import JobProfile from '../JobProfile';
import { showDialog, hideDialog, isDialogOpen } from '../../actions/dialogs';
import { fetchJobProfiles, setJobProfile } from '../../actions/jobprofiles';
import Dashboard from '../Dashboard';
import { hasPermission } from '../../utils/authenticated';
import { sortStrings, contains, getOrderDirection, id } from '../../utils/helper';
import Loading from '../Common/Loading';
import { removeJobProfileRoutine } from '../../routines';

import { SECONDARY } from '../styles';

import styles from './styles';

const getTableColumns = (editable, onEdit, onRemove) => {
  return _.flatten([
    {
      key: 'name',
      label: 'Name',
      render: (name) => name,
      sortable: true,
      style: styles.nameCol,
    }, {
      key: 'stages',
      label: 'Stages',
      render: (stages) => <div style={styles.stages}>
        {(stages || []).map((stage, idx) => <Chip
          labelStyle={styles.chipLabelStyle}
          style={styles.chip}
          key={stage.id}
        >
            { stage.icon ? <Avatar size={32} backgroundColor={SECONDARY} icon={<Icon name={stage.icon} />}></Avatar>
                : <Avatar size={32} backgroundColor={SECONDARY}>{idx + 1}</Avatar> }
          {stage.name}
        </Chip>)}
      </div>,
      style: {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      },
    }, {
      key: 'action',
      label: '',
      render: (a, job) => {
        return (<span>{ editable ? <IconButton
          tooltip="Edit"
          tooltipPosition="bottom-center"
          onClick={(evt) => onEdit(evt, job)}
        >
          <FontIcon
            className="material-icons"
            style={styles.icon}
          >edit</FontIcon>
        </IconButton> : null }
          { editable ? <IconButton
            tooltip="Remove"
            tooltipPosition="bottom-center"
            onClick={(evt) => onRemove(evt, job)}
          >
            <FontIcon
              className="material-icons"
              style={styles.icon}
            >delete</FontIcon>
          </IconButton> : null }
          </span>);
      },
      alignRight: true,
      style: {
        width: 150,
      },
    },
  ]);
};

const getSorter = (sorter = (a, b) => 0, order = 'asc') => {
  return (a, b) => {
    let result = sorter(a, b);

    if (result === 0) {
      result = sortStrings(a.name, b.name);
    }

    return result * getOrderDirection(order);
  };
};

const getJobProfilesFilter = (filter) => {
  return (profile) => {
    if (filter) {
      return contains(profile.name, filter);
    }

    return true;
  };
};

class JobProfiles extends Component {
  static propTypes = {
    fetchJobProfiles: PropTypes.func.isRequired,
    removeJobProfile: PropTypes.func.isRequired,
    setJobProfile: PropTypes.func.isRequired,
    jobProfiles: PropTypes.array.isRequired,
    loading: PropTypes.bool,

    dialogs: PropTypes.object.isRequired,
    showDialog: PropTypes.func.isRequired,
    hideDialog: PropTypes.func.isRequired,
  };

  state = {
    page: 1,
    limit: 10,
    sortBy: getSorter(),
    order: 'asc',
    data: [],
  };

  componentWillMount() {
    this.props.fetchJobProfiles();

    this.setState({
      editable: hasPermission(['Administrator']),
      data: this.props.jobProfiles.sort(this.state.sortBy),
    });
  }

  componentWillReceiveProps(nextProps) {
    this.setState({
      data: nextProps.jobProfiles.sort(this.state.sortBy),
    });
  }

  handleFilterValueChange = (filter) => {
    const { jobProfiles } = this.props;
    const { page, limit, sortBy } = this.state;

    const data = jobProfiles.filter(getJobProfilesFilter(filter ? filter.trim().toLowerCase() : '')).sort(sortBy);
    this.setState({
      page: Math.min(page, Math.floor(data.length / limit) + 1),
      data,
    });
  };

  handleSortOrderChange = (key, order) => {
    let sortBy = getSorter();
    switch (key) {
      case 'name':
        sortBy = getSorter((a, b) => sortStrings(a.name, b.name), order);
        break;
      default:
        break;
    }

    this.setState({
      data: this.state.data.sort(sortBy),
      sortBy,
    });
  };

  handlePreviousPageClick = () => {
    this.setState({
      page: Math.max(1, this.state.page - 1),
    });
  };

  handleNextPageClick = () => {
    const { data, page, limit } = this.state;

    this.setState({
      page: Math.min(Math.ceil(data.length / limit), page + 1),
    });
  };

  handleRowSizeChange = (a, limit) => {
    this.setState({
      limit,
      page: 1,
    });
  };

  handleAdd = () => {
    this.props.setJobProfile({
      id: id(),
    });
    this.props.showDialog('jobprofile');
  }

  handleEdit = (evt, profile) => {
    evt.stopPropagation();
    this.props.setJobProfile(profile);
    this.props.showDialog('jobprofile');
  };

  handleRemove = (evt, profile) => {
    evt.stopPropagation();

    const context = {
      type: 'warning',
      title: 'Remove Job Profile?',
      message: 'Are you sure you wish to remove this job profile?',
      buttons: [
        <FlatButton
          label="Cancel"
          onClick={() => this.props.hideDialog('confirm')}
        />,
        <FlatButton
          label="Remove"
          onClick={() => {
            this.props.hideDialog('confirm');
            this.props.removeJobProfile(profile);
          }}
        />,
      ],
    };

    this.props.showDialog('confirm', context);
  };

  renderNoData() {
    return (<TableRow key="nodata">
      <TableRowColumn colSpan={3} style={styles.noDataRow}>
        <FontIcon className="material-icons" style={styles.noData}>view_list</FontIcon>
        <div>No job profiles are available</div>
      </TableRowColumn>
    </TableRow>);
  }

  render() {
    const { loading } = this.props;
    const { editable, page, limit, data } = this.state;

    return (
      <Dashboard title="Job Profiles">
        { !loading ? <DataTables
          height={'auto'}
          showRowHover
          columns={getTableColumns(editable, this.handleEdit, this.handleRemove)}
          data={data.slice((page - 1) * limit, page * limit)}
          onFilterValueChange={this.handleFilterValueChange}
          onSortOrderChange={this.handleSortOrderChange}
          onPreviousPageClick={this.handlePreviousPageClick}
          onNextPageClick={this.handleNextPageClick}
          onRowSizeChange={this.handleRowSizeChange}
          onRowSelection={this.handleRowSelection}
          page={page}
          rowSize={limit}
          rowSizeList={[10, 15, 25, 50, 100]}
          count={data.length}
          filterHintText="Filter"
          headerToolbarMode="filter"
          showHeaderToolbar
        /> : <Loading /> }

        <FloatingActionButton style={styles.addItem} secondary onClick={this.handleAdd}>
          <ContentAdd />
        </FloatingActionButton>

        { isDialogOpen(this.props.dialogs, 'jobprofile') ? <JobProfile /> : null }
      </Dashboard>
    );
  }
}

function mapStateToProps(state) {
  return {
    jobProfiles: state.get('jobprofiles').get('jobProfiles').toJS().filter((profile) => !profile.deleted),
    loading: state.get('jobprofiles').get('loading'),
    dialogs: state.get('dialogs').get('dialogs').toJS(),
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators({
    fetchJobProfiles,
    removeJobProfile: removeJobProfileRoutine.trigger,
    setJobProfile,
    showDialog,
    hideDialog,
  }, dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(JobProfiles);



// WEBPACK FOOTER //
// ./src/components/JobProfiles/index.js